/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { Component, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createRouterSelector, type Match } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	defaultRegistry,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import type { Actions } from '../actions/page-events/index.tsx';
import { useSpaState, useSpaStateActions } from '../common/index.tsx';
import Store from '../store.tsx';
import type { State } from '../types.tsx';

export type SpaStateContainerProps = {
	scope?: string;
	appId: string;
};
export const SpaStateContainer: OverrideContainerComponent<SpaStateContainerProps> =
	createContainer<State, Actions, SpaStateContainerProps>(Store);
export const SpaStateAppStart = (): null => {
	// oxlint-disable-next-line react/react-compiler
	const startRef = useRef(Date.now());
	const [{ visitedPages }, { setAppStart }] = useSpaState();
	// oxlint-disable-next-line react/react-compiler
	useMemo(() => {
		// oxlint-disable-next-line react/react-compiler
		startRef.current = Date.now();

		// oxlint-disable-next-line react-hooks/exhaustive-deps, react/react-compiler
	}, [visitedPages]);
	useLayoutEffect(() => {
		setAppStart(startRef.current);
	}, [setAppStart, visitedPages]);
	return null;
};

const useRouterMatchSelector = createRouterSelector((state) => state.match);
const useSpaStatePagesCount = createHook(Store, {
	selector: ({ visitedPages }) => visitedPages.length,
});
export const SpaStateAppStartWithMatchCheck = (): React.JSX.Element => {
	const match = useRouterMatchSelector();
	const [count, { setAppStart }] = useSpaStatePagesCount();
	return (
		<SpaStateAppStartChild visitedPagesLength={count} onSetAppStart={setAppStart} match={match} />
	);
};

export function SpaStatePageReady(): null {
	const { setAppReady } = useSpaStateActions();
	useEffect(() => {
		setAppReady();
	}, [setAppReady]);
	return null;
}

export type SpaStatePageReadyOnUpdateProps = {
	/**
	 * discriminator for when `setAppReady` should be re-triggered
	 */
	updateKey: unknown;
};

export function SpaStatePageReadyOnUpdate({ updateKey }: SpaStatePageReadyOnUpdateProps): null {
	const { setAppReady } = useSpaStateActions();
	useEffect(() => {
		setAppReady();
	}, [setAppReady, updateKey]);
	return null;
}

export function SpaStatePageErrorReady(): null {
	const { setAppErrorReady } = useSpaStateActions();
	useEffect(() => {
		setAppErrorReady();
	}, [setAppErrorReady]);
	return null;
}
type SpaStateAppStartChildProps = {
	visitedPagesLength: number;
	match: Match | null;
	onSetAppStart: (at: number) => void;
};
type SpaStateAppStartChildState = {
	visitedPagesLength: number;
	started: boolean;
	match: Match | null;
};

/**
 * Manages the application startup state within a SPA by responding to navigation and page visit changes. Ensures the app state is reset appropriately to maintain a seamless user experience.
 */
// oxlint-disable-next-line react/prefer-function-component
export class SpaStateAppStartChild extends Component<
	SpaStateAppStartChildProps,
	SpaStateAppStartChildState
> {
	/** so the challenge we are facing is that, we need to reset page when there's a visitedPagesLength change and a route change,
	 * but they don't always happen at the the same time during the current spa router situation. The following code does this:
	 *
	 * if first render: set app start
	 *
	 * if subsequently render with only visitedPagesLength change, ignore this updates, cache the old visitedPagesLength,
	 * wait for incoming match change
	 *
	 * if subsequently render with only match change, ignore this updates, cache the old match, wait for incoming
	 * visitedPagesLength change
	 *
	 *  if incoming changes contain both route and length change, we update state and start app
	 *
	 */

	state = {
		visitedPagesLength: 0,
		started: false,
		match: null,
	};

	static getDerivedStateFromProps(
		props: SpaStateAppStartChildProps,
		state: SpaStateAppStartChildState,
	): SpaStateAppStartChildState {
		const visitedPageLengthHasChanged = props.visitedPagesLength !== state.visitedPagesLength;
		const matchHasChanged = props.match !== state.match;
		const setAppStart = !state.started || (visitedPageLengthHasChanged && matchHasChanged);
		if (setAppStart) {
			props.onSetAppStart(Date.now());
		}

		// On transition visitedPageLength will always change before match, the only exception is when there is page
		// ids (e.g. issue types). When redirecting, the length doesn't change while the match changes (from issuetype to issuetype/xxxxx).
		// so if match changed without visitedPageLength changing, cache the new match and wait for both to update.
		if (setAppStart || !visitedPageLengthHasChanged) {
			return {
				started: true,
				visitedPagesLength: props.visitedPagesLength,
				match: props.match,
			};
		}

		// if visitedPageLength changed, ignore and wait for update in match (which will trigger setAppStart next)
		return state;
	}

	render(): null {
		return null;
	}
}
// helper function to get the last visited page (hacky way to do it but couldnt find more reliable and correct
// way to do it).
export const getSpaStateLastUserLocation = (): string | undefined => {
	const { visitedPages } = defaultRegistry.getStore(Store).storeState.getState();
	return visitedPages.length ? visitedPages[visitedPages.length - 1].userLocation : '';
};

/**
 * Imperative check for the initial SPA render (no route transition yet).
 * Returns `true` when no pages have been visited.
 */
export const getIsInitialSpaRender = (): boolean => {
	const { visitedPages } = defaultRegistry.getStore(Store).storeState.getState();
	return visitedPages.length === 0;
};
